import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../utils/axiosBaseQuery";
import { getApiUrl } from "../config/env";

export const adminApi = createApi({
    reducerPath: "adminApi",
    baseQuery: axiosBaseQuery({ 
        baseUrl: getApiUrl()
    }),
    tagTypes: ["AdminMetrics", "Auth", "ChannelStats", "DailyMetrics", "MetricsHistory"],
    endpoints: (builder) => ({
        login: builder.mutation({
            query: (credentials) => ({
                url: "account/login",
                method: "POST",
                body: {
                    payload: credentials
                }
            }),
            providesTags: ["Auth"],
        }),
        getUsersMetrics: builder.query({
            query: (params = {}) => ({
                url: "admin/users/metrics",
                method: "GET",
                params: {
                    page: params.page || 1,
                    limit: params.limit || 50,
                    ...params
                }
            }),
            providesTags: ["AdminMetrics"],
            transformResponse: (response) => {
                if (response.success && response.data && response.data.metrics) {
                    return {
                        metrics: response.data.metrics.map(metric => ({
                            _id: metric._id,
                            user: metric.user ? {
                                name: metric.user.username || 'Unknown User',
                                email: metric.user.email || 'No email',
                                avatar: metric.user.avatar || null,
                                id: metric.user.id || metric.user._id
                            } : {
                                name: 'Anonymous User',
                                email: 'No email',
                                avatar: null,
                                id: 'anonymous'
                            },
                            session_id: metric.session_id || 'N/A',
                            post_count: metric.post_count || 0,
                            comment_count: metric.comment_count || 0,
                            likes_count: metric.likes_count || 0,
                            channels_joined: metric.channels_joined || 0,
                            no_of_anon_link_gen: metric.no_of_anon_link_gen || 0,
                            no_of_anon_msg_received: metric.no_of_anon_msg_received || 0,
                            message_sent_count: metric.message_sent_count || 0,
                            message_received_count: metric.message_received_count || 0,
                            is_premium: metric.is_premium || false,
                            engagement_score: metric.engagement_score || 0,
                            last_activity: metric.last_activity || new Date().toISOString(),
                            days_active: metric.days_active || 0,
                            computed_at: metric.computed_at || new Date().toISOString(),
                            date: new Date(metric.computed_at || metric.created_at).toLocaleDateString('en-GB'),
                            computation_duration_ms: metric.computation_duration_ms || 0,
                            is_latest: metric.is_latest || true
                        })),
                        pagination: response.data.pagination || {
                            page: 1,
                            limit: 50,
                            total: 0,
                            totalPages: 1,
                            hasMore: false
                        }
                    };
                }
                return { metrics: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 1, hasMore: false } };
            }
        }),
        getChannelStats: builder.query({
            query: (params = {}) => {
                const queryParams = {};
                
                // Handle single date filter
                if (params.date) {
                    queryParams.date = params.date;
                }
                
                // Handle date range filters
                if (params.startDate) {
                    queryParams.startDate = params.startDate;
                }
                
                if (params.endDate) {
                    queryParams.endDate = params.endDate;
                }
                
                return {
                    url: "admin/channel/stats",
                    method: "GET",
                    params: queryParams
                };
            },
            providesTags: ["ChannelStats"],
            transformResponse: (response) => {
                if (response.success && response.data) {
                    return response.data.map(channel => {
                        const channelName = Object.keys(channel)[0];
                        const stats = channel[channelName];
                        return {
                            name: channelName,
                            posts: stats.posts || 0,
                            likes: stats.likes || 0,
                            comments: stats.comments || 0,
                            totalEngagement: (stats.posts || 0) + (stats.likes || 0) + (stats.comments || 0)
                        };
                    }).sort((a, b) => b.totalEngagement - a.totalEngagement); // Sort by total engagement
                }
                return [];
            }
        }),
        refreshUsersMetrics: builder.mutation({
            query: () => ({
                url: "admin/users/metrics/refresh",
                method: "POST",
            }),
            invalidatesTags: ["AdminMetrics"],
        }),
        
        // New Daily Metrics Endpoints
        getUserDailyMetrics: builder.query({
            query: ({ userId, date, limit = 30 }) => {
                const params = { limit };
                if (date) {
                    params.date = date;
                }
                return {
                    url: `admin/user/${userId}/metrics/daily`,
                    method: "GET",
                    params
                };
            },
            providesTags: (result, error, { userId }) => [
                { type: "DailyMetrics", id: userId },
                "DailyMetrics"
            ],
            transformResponse: (response) => {
                if (response.success && response.data) {
                    return response.data.map(metric => ({
                        _id: metric._id,
                        user: metric.user ? {
                            name: metric.user.username || 'Unknown User',
                            email: metric.user.email || 'No email',
                            avatar: metric.user.avatar || null,
                            id: metric.user.id || metric.user._id
                        } : null,
                        session_id: metric.session_id || 'N/A',
                        post_count: metric.post_count || 0,
                        comment_count: metric.comment_count || 0,
                        likes_count: metric.likes_count || 0,
                        channels_joined: metric.channels_joined || 0,
                        no_of_anon_link_gen: metric.no_of_anon_link_gen || 0,
                        no_of_anon_msg_received: metric.no_of_anon_msg_received || 0,
                        message_sent_count: metric.message_sent_count || 0,
                        message_received_count: metric.message_received_count || 0,
                        is_premium: metric.is_premium || false,
                        engagement_score: metric.engagement_score || 0,
                        metrics_date: metric.metrics_date,
                        metrics_type: metric.metrics_type || 'daily',
                        computed_at: metric.computed_at || new Date().toISOString(),
                        date: new Date(metric.metrics_date || metric.computed_at).toLocaleDateString('en-GB'),
                        computation_duration_ms: metric.computation_duration_ms || 0,
                        // Daily-specific fields
                        daily_posts: metric.daily_posts,
                        daily_comments: metric.daily_comments,
                        daily_likes: metric.daily_likes,
                        daily_messages_sent: metric.daily_messages_sent,
                        daily_messages_received: metric.daily_messages_received,
                        daily_engagement_score: metric.daily_engagement_score
                    }));
                }
                return [];
            }
        }),

        getMetricsByDateRange: builder.query({
            query: ({ startDate, endDate, userId, metricsType = 'daily', page = 1, limit = 50 }) => {
                const params = {
                    startDate,
                    endDate,
                    metricsType,
                    page,
                    limit
                };
                if (userId) {
                    params.userId = userId;
                }
                return {
                    url: "admin/metrics/date-range",
                    method: "GET",
                    params
                };
            },
            providesTags: ["MetricsHistory"],
            transformResponse: (response) => {
                if (response.success && response.data) {
                    return {
                        metrics: response.data.metrics.map(metric => ({
                            _id: metric._id,
                            user: metric.user ? {
                                name: metric.user.username || 'Unknown User',
                                email: metric.user.email || 'No email',
                                avatar: metric.user.avatar || null,
                                id: metric.user.id || metric.user._id
                            } : null,
                            session_id: metric.session_id || 'N/A',
                            post_count: metric.post_count || 0,
                            comment_count: metric.comment_count || 0,
                            likes_count: metric.likes_count || 0,
                            channels_joined: metric.channels_joined || 0,
                            no_of_anon_link_gen: metric.no_of_anon_link_gen || 0,
                            no_of_anon_msg_received: metric.no_of_anon_msg_received || 0,
                            message_sent_count: metric.message_sent_count || 0,
                            message_received_count: metric.message_received_count || 0,
                            is_premium: metric.is_premium || false,
                            engagement_score: metric.engagement_score || 0,
                            metrics_date: metric.metrics_date,
                            metrics_type: metric.metrics_type || 'daily',
                            computed_at: metric.computed_at || new Date().toISOString(),
                            date: new Date(metric.metrics_date || metric.computed_at).toLocaleDateString('en-GB'),
                            computation_duration_ms: metric.computation_duration_ms || 0,
                            // Daily-specific fields
                            daily_posts: metric.daily_posts,
                            daily_comments: metric.daily_comments,
                            daily_likes: metric.daily_likes,
                            daily_messages_sent: metric.daily_messages_sent,
                            daily_messages_received: metric.daily_messages_received,
                            daily_engagement_score: metric.daily_engagement_score
                        })),
                        pagination: response.data.pagination || {
                            page: 1,
                            limit: 50,
                            total: 0,
                            totalPages: 1,
                            hasMore: false
                        },
                        dateRange: response.data.dateRange || {
                            startDate: null,
                            endDate: null
                        }
                    };
                }
                return { 
                    metrics: [], 
                    pagination: { page: 1, limit: 50, total: 0, totalPages: 1, hasMore: false },
                    dateRange: { startDate: null, endDate: null }
                };
            }
        }),

        getUserMetrics: builder.query({
            query: (userId) => ({
                url: `admin/user/${userId}/metrics`,
                method: "GET"
            }),
            providesTags: (result, error, userId) => [
                { type: "AdminMetrics", id: userId },
                "AdminMetrics"
            ],
            transformResponse: (response) => {
                if (response.success && response.data) {
                    const metric = response.data;
                    return {
                        _id: metric._id,
                        user: metric.user ? {
                            name: metric.user.username || 'Unknown User',
                            email: metric.user.email || 'No email',
                            avatar: metric.user.avatar || null,
                            id: metric.user.id || metric.user._id
                        } : null,
                        session_id: metric.session_id || 'N/A',
                        post_count: metric.post_count || 0,
                        comment_count: metric.comment_count || 0,
                        likes_count: metric.likes_count || 0,
                        channels_joined: metric.channels_joined || 0,
                        no_of_anon_link_gen: metric.no_of_anon_link_gen || 0,
                        no_of_anon_msg_received: metric.no_of_anon_msg_received || 0,
                        message_sent_count: metric.message_sent_count || 0,
                        message_received_count: metric.message_received_count || 0,
                        is_premium: metric.is_premium || false,
                        engagement_score: metric.engagement_score || 0,
                        last_activity: metric.last_activity || new Date().toISOString(),
                        days_active: metric.days_active || 0,
                        computed_at: metric.computed_at || new Date().toISOString(),
                        date: new Date(metric.computed_at || metric.created_at).toLocaleDateString('en-GB'),
                        computation_duration_ms: metric.computation_duration_ms || 0,
                        is_latest: metric.is_latest || true
                    };
                }
                return null;
            }
        }),

        computeDailyMetrics: builder.mutation({
            query: (date) => ({
                url: `admin/compute-daily/${date}`,
                method: "POST"
            }),
            invalidatesTags: ["DailyMetrics", "MetricsHistory", "AdminMetrics"],
        }),

        refreshUserMetrics: builder.mutation({
            query: (userId) => ({
                url: `admin/metrics/user/${userId}/metrics/refresh`,
                method: "POST"
            }),
            invalidatesTags: (result, error, userId) => [
                { type: "AdminMetrics", id: userId },
                "AdminMetrics",
                "DailyMetrics"
            ],
        }),

        getMetricsHistory: builder.query({
            query: ({ userId, days = 30, limit = 100 }) => ({
                url: `metrics/user/${userId}/metrics/history`,
                method: "GET",
                params: { days, limit }
            }),
            providesTags: (result, error, { userId }) => [
                { type: "MetricsHistory", id: userId },
                "MetricsHistory"
            ],
            transformResponse: (response) => {
                if (response.success && response.data) {
                    return response.data.map(metric => ({
                        _id: metric._id,
                        user: metric.user ? {
                            name: metric.user.username || 'Unknown User',
                            email: metric.user.email || 'No email',
                            avatar: metric.user.avatar || null,
                            id: metric.user.id || metric.user._id
                        } : null,
                        session_id: metric.session_id || 'N/A',
                        post_count: metric.post_count || 0,
                        comment_count: metric.comment_count || 0,
                        likes_count: metric.likes_count || 0,
                        channels_joined: metric.channels_joined || 0,
                        no_of_anon_link_gen: metric.no_of_anon_link_gen || 0,
                        no_of_anon_msg_received: metric.no_of_anon_msg_received || 0,
                        message_sent_count: metric.message_sent_count || 0,
                        message_received_count: metric.message_received_count || 0,
                        is_premium: metric.is_premium || false,
                        engagement_score: metric.engagement_score || 0,
                        last_activity: metric.last_activity || new Date().toISOString(),
                        days_active: metric.days_active || 0,
                        computed_at: metric.computed_at || new Date().toISOString(),
                        date: new Date(metric.computed_at || metric.created_at).toLocaleDateString('en-GB'),
                        computation_duration_ms: metric.computation_duration_ms || 0,
                        is_latest: metric.is_latest || true
                    }));
                }
                return [];
            }
        }),

        getMetricsStats: builder.query({
            query: () => ({
                url: "metrics/stats",
                method: "GET"
            }),
            providesTags: ["AdminMetrics"],
            transformResponse: (response) => {
                if (response.success && response.data) {
                    return response.data;
                }
                return {
                    totalUsers: 0,
                    premiumUsers: 0,
                    totalPosts: 0,
                    totalComments: 0,
                    avgEngagement: 0,
                    maxEngagement: 0,
                    activeUsers: 0
                };
            }
        }),
    }),
});

export const {
    useLoginMutation,
    useGetUsersMetricsQuery,
    useGetChannelStatsQuery,
    useRefreshUsersMetricsMutation,
    // New daily metrics hooks
    useGetUserDailyMetricsQuery,
    useGetMetricsByDateRangeQuery,
    useGetUserMetricsQuery,
    useComputeDailyMetricsMutation,
    useRefreshUserMetricsMutation,
    useGetMetricsHistoryQuery,
    useGetMetricsStatsQuery,
} = adminApi;